/**
 * Job Detail — one job on one day: address + directions, job details, own hours,
 * files shared with this role, and a shortcut to add a site note.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { MapPin, Navigation, Phone, Loader2, Camera, ClipboardList, KeyRound, User } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { PortalSharedFiles } from '../../components/portal/PortalSharedFiles';
import { HoursCard } from '../../components/portal/HoursCard';

const todayIso = () => format(new Date(), 'yyyy-MM-dd');

function Detail({ icon: Icon, label, value, href, testId }) {
  return (
    <div className="flex items-start gap-3" data-testid={testId}>
      <Icon className="w-4 h-4 text-[#F5A623] mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">{label}</p>
        {value ? (
          href ? <a href={href} className="text-white text-sm underline-offset-2 hover:underline break-words">{value}</a>
            : <p className="text-white text-sm whitespace-pre-wrap break-words">{value}</p>
        ) : <p className="text-gray-500 text-sm italic">Not provided</p>}
      </div>
    </div>
  );
}

export function PortalJobDetail() {
  const { api } = useAuth();
  const { projectId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const date = params.get('date') || todayIso();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/portal/jobs/${projectId}?date=${date}`);
      setJob(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load this job');
      navigate('/portal/schedule', { replace: true });
    } finally { setLoading(false); }
  }, [api, projectId, date, navigate]);
  useEffect(() => { load(); }, [load]);

  if (loading || !job) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#F5A623]" /></div>;

  const p = job.project;
  const addressText = [p.address, p.town_city, p.postcode].filter(Boolean).join(', ');
  const mapsHref = addressText ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressText)}` : null;
  const phoneHref = p.site_contact_phone ? `tel:${p.site_contact_phone.replace(/\s+/g, '')}` : null;
  const con = (p.project_types || []).includes('CON') || p.project_type === 'CON' || (p.project_ref || '').startsWith('LC');

  return (
    <div className="p-4 space-y-4 pb-28" data-testid="portal-job-detail">
      {/* Header */}
      <div data-testid="job-header">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-lg font-black ${con ? 'text-blue-400' : 'text-[#F5A623]'}`} data-testid="job-code">{p.project_code || p.project_ref}</span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${con ? 'bg-blue-500/15 text-blue-300 border-blue-500/30' : 'bg-[#F5A623]/15 text-[#F5A623] border-[#F5A623]/30'}`}>{con ? 'Construction' : 'Developments'}</span>
        </div>
        <h1 className="text-white text-xl font-bold leading-tight mt-0.5" data-testid="job-name">{p.name || p.client_name || p.project_ref}</h1>
        <p className="text-gray-400 text-sm mt-1" data-testid="job-date">{format(parseISO(job.date), 'EEEE d MMMM yyyy')}{job.is_today ? ' · Today' : ''}{!job.is_scheduled ? ' · not on your schedule this day' : ''}</p>
      </div>

      {/* Address + directions */}
      <Card className="bg-[#2A3240] border-white/10" data-testid="job-address-card">
        <CardContent className="p-4 flex items-start gap-3">
          <MapPin className="w-5 h-5 text-[#F5A623] mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm">{p.address || 'Address not set'}</p>
            {p.town_city && <p className="text-gray-400 text-sm">{p.town_city}</p>}
            {p.postcode && <p className="text-gray-300 text-sm font-medium">{p.postcode}</p>}
          </div>
          {mapsHref && (
            <a href={mapsHref} target="_blank" rel="noreferrer" className="h-10 px-3 rounded-lg bg-[#F5A623] text-[#1E2530] font-semibold flex items-center gap-1.5 text-sm active:scale-95 shrink-0" data-testid="job-directions-btn">
              <Navigation className="w-4 h-4" /> Directions
            </a>
          )}
        </CardContent>
      </Card>

      {/* Job details */}
      <Card className="bg-[#2A3240] border-white/10" data-testid="job-details-card">
        <CardHeader className="pb-2"><CardTitle className="text-white text-sm flex items-center gap-2"><ClipboardList className="w-4 h-4 text-[#F5A623]" /> Job details</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Detail icon={ClipboardList} label="Scope" value={p.scope} testId="job-scope" />
          <Detail icon={User} label="Site contact" value={p.site_contact_name ? `${p.site_contact_name}${p.site_contact_phone ? ` · ${p.site_contact_phone}` : ''}` : null} href={phoneHref} testId="job-site-contact" />
          {!p.site_contact_name && p.site_contact_phone && <Detail icon={Phone} label="Site contact phone" value={p.site_contact_phone} href={phoneHref} testId="job-site-phone" />}
          <Detail icon={KeyRound} label="Access / parking" value={p.access_notes} testId="job-access-notes" />
        </CardContent>
      </Card>

      {/* Hours */}
      <HoursCard job={job} onChanged={load} />

      {/* Files */}
      <PortalSharedFiles projectId={projectId} title="Files & drawings" emptyText="No files shared for this job yet." hint={null} />

      {/* Site note */}
      <div className="pt-1">
        <Button onClick={() => navigate(`/portal/site-notes/new?project=${projectId}`)} className="w-full h-14 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-bold text-base shadow-xl" data-testid="job-add-note-btn">
          <Camera className="w-5 h-5 mr-2" /> Add site note
        </Button>
      </div>
    </div>
  );
}
